import Calendario from './calendario.vue';
import CalendarioNoDisponible from './calendario-no-disponible.vue';
import Error403 from './../403.vue';
import RoutesUtils from './../../js/RoutesUtils.js';
import RhVacaciones from './../../js/db/RhVacaciones.js';
import UsuarioActualizado from './../../js/usuarioActualizado.js';
import { crearBarrera, textoDelFallo, TOPE_BARRERA_MS } from './../../js/barreraRuta.js';

/**
 * #49 · Barrera de entrada del calendario: resuelve el acceso Y el interruptor
 * `rh.vacaciones.bloqueo-generico` ANTES de montar la pantalla.
 *
 * POR QUÉ ESTO Y NO UNA ESPERA EN CADA ESCRITOR (decisión de diseño de Donchurru y dirección,
 * 01/10/2026 en la incidencia #49):
 *
 * El calendario preguntaba el interruptor en su `created()` y arrancaba con `false` mientras
 * llegaba la respuesta. Esa ventana —«todavía no se sabe» tratado como «apagado»— es la que
 * producía un hallazgo por ronda de revisión: un gesto a medias, un arrastre que sale del
 * calendario, un borde que se estira, un formulario que se limpia mientras se espera. Taparlos uno
 * a uno desplaza el problema: mientras la ventana exista, siempre queda una cosa más.
 *
 * Aquí la ventana no existe. Cuando el componente se monta, el valor del interruptor ya está
 * decidido y viaja como `prop`, así que los cuatro caminos de escritura y todos los formularios
 * leen un valor que no puede cambiar bajo sus pies. No hay nada que reconciliar porque no hay nada
 * que llegue tarde.
 *
 * REGLAS QUE FIJA LA DECISIÓN, Y DÓNDE SE CUMPLEN:
 *
 * - Las DOS rutas del calendario (`/calendario/` y `/calendario/:username/`) entran por aquí, y no
 *   hay ninguna otra forma de montar `calendario.vue` (ver `routes.js`).
 * - Desconocido, error, timeout o dato inválido NO equivalen a apagado: se asume el valor más
 *   restrictivo. `RhVacaciones.bloqueoGenerico()` ya no se traga los fallos devolviendo false, que
 *   era de donde salía esa equivalencia.
 * - Cero escrituras mientras no se sabe: cuando el calendario se monta, el valor ya está decidido.
 * - Una sola consulta al endpoint que ya existía, en la entrada. Ni una por escritura.
 * - Si se abandona la navegación, la respuesta tardía se descarta: lo decide el ciclo real del
 *   router, no una lista de sitios por los que se puede salir.
 *
 * [#65, 02/10/2026] El acceso se comprueba AHORA con el usuario fresco, no con el menú que quedó
 * guardado de la sesión anterior: `UsuarioActualizado.esperar()` va delante de todo lo demás. El
 * motivo, en `usuarioActualizado.js`. El mecanismo de espera —tope, descarte de navegaciones
 * abandonadas, cerrojo del router— es el mismo de siempre, ahora compartido en `barreraRuta.js`.
 *
 * [#174, 03/10/2026] LA BARRERA RESUELVE DOS COSAS Y SOLO UNA PUEDE DEGRADAR:
 *
 * Hasta ahora cualquiera de las dos que fallase sustituía la pantalla entera por
 * `calendario-no-disponible`. Eso era desproporcionado para el interruptor: gobierna UN caso
 * —editar T0006 y los festivos generados— y por él se perdían las fichas de inspección, los
 * desplazamientos, los fichajes, los festivos, las métricas de cabecera, el selector de empleado y
 * el cambio de semana, en la pantalla que el equipo de campo usa a diario.
 *
 * - **El interruptor** (`bloqueo-generico`): si falla o no contesta, el calendario se monta igual
 *   con el valor MÁS RESTRICTIVO y un aviso discreto en la cabecera. Un dato que falta y que solo
 *   gobierna un caso no justifica cerrar la pantalla; lo que no cambia es que lo desconocido
 *   siga siendo lo restrictivo.
 * - **El acceso** (`UsuarioActualizado.esperar()` + `RoutesUtils.tieneAcceso()`): si falla o
 *   expira, se sigue enseñando `calendario-no-disponible`. No es un dato que falte, es no saber
 *   quién está mirando, y montar el calendario con los permisos de la sesión anterior es justo lo
 *   que la #49 vino a eliminar. Decisión de dirección (JJCS, 03/10/2026 en la #174).
 *
 * El servidor conserva su propio bloqueo: esto decide lo que se PINTA, no lo que se permite. Si el
 * interruptor cambiara después de montar el calendario, el 4xx del servidor sigue siendo la
 * barrera de verdad. Y por eso degradar es seguro: con el valor restrictivo el front esconde lo que
 * el servidor podría estar aceptando —se pierde trabajo, no se cuela ninguno—.
 */

export { TOPE_BARRERA_MS };

/**
 * [#174] El valor del interruptor cuando no se ha podido comprobar: el más restrictivo. Si no se
 * sabe si las vacaciones se pueden editar, no se editan.
 */
const BLOQUEO_CUANDO_NO_SE_SABE = true;

/**
 * [#271] Y el del camino manual, por lo mismo: si no se sabe si Dirección lo ha cerrado, el
 * calendario no ofrece poner vacaciones a mano. Se pierde una opción, no se cuela ninguna: el
 * servidor la rechazaría igual.
 */
const CAMINO_MANUAL_APAGADO_CUANDO_NO_SE_SABE = true;

/**
 * [#174] Deja dicho en el `routeTo` de ESTA navegación que el acceso ya quedó resuelto.
 *
 * Hace falta porque el tope de la barrera cubre las dos esperas y `alFallar` recibe el error sin
 * saber cuál de las dos se atascó — y desde ahora las dos no se tratan igual. Si el tope salta con
 * el acceso ya resuelto, lo que no ha contestado es el interruptor.
 *
 * Va en `routeTo` y no en una variable de este módulo porque el resolvedor se comparte entre las
 * dos rutas y entre navegaciones: una variable la pisaría la entrada de al lado, y justo el caso
 * peligroso —marcada por otra navegación, expirada en el acceso en la mía— acabaría montando el
 * calendario sin saber quién mira. Framework7 crea un `routeTo` nuevo en cada navegación
 * (`findMatchingRoute` devuelve un literal y `navigate.js`/`back.js` se lo pasan tal cual al
 * resolvedor `async`), así que dos entradas a la misma url tienen cada una su marca.
 */
function marcarAccesoResuelto(routeTo) {
    if (routeTo) routeTo.__accesoCalendarioResuelto = true;
}

function accesoResuelto(routeTo) {
    return !!(routeTo && routeTo.__accesoCalendarioResuelto);
}

/**
 * [#174] El calendario con lo que se sepa del interruptor. `avisoBloqueoVacaciones` es lo que la
 * cabecera usa para decir, sin bloquear nada, que la edición de vacaciones no está disponible.
 *
 * [#271] Y el camino manual, que llega en la misma consulta y por eso se decide a la vez: los tres
 * props salen siempre juntos de aquí.
 */
function montarCalendario(activo, avisoInterruptor, caminoManualApagado) {
    return {
        component: Calendario,
        props: { bloqueoVacaciones: activo, avisoBloqueoVacaciones: avisoInterruptor, caminoManualApagado: caminoManualApagado }
    };
}

/**
 * Construye el resolvedor `async` de una ruta del calendario. Sustituye a
 * `RoutesUtils.checkPermission('calendario', roles, Calendario)`, cuyo control de acceso repite tal
 * cual: lo único que añade es el interruptor, y lo añade DESPUÉS del acceso —a quien no puede
 * entrar no se le consulta nada—.
 */
export function barreraBloqueoVacaciones(roles) {
    return crearBarrera({
        tope: TOPE_BARRERA_MS,
        pedir: function(routeTo) {
            // El acceso se decide con el usuario de esta carga, y solo entonces se pregunta por el
            // interruptor. El tope sigue cubriendo las dos esperas, igual que antes: lo que cambia
            // es qué se pinta si salta.
            return UsuarioActualizado.esperar().then(function() {
                if (!RoutesUtils.tieneAcceso(routeTo.path, roles)) {
                    return { prohibido: true };
                }
                // Desde aquí ya se sabe quién mira y que puede entrar. Lo único que queda por
                // saber es el interruptor, y eso ya no impide montar.
                marcarAccesoResuelto(routeTo);
                // [#271] Una sola consulta para los dos interruptores: el bloqueo de la #49 y el
                // camino manual que acciona Dirección.
                return RhVacaciones.interruptoresCalendario().then(function(interruptores) {
                    return { activo: interruptores.activo, caminoManualApagado: interruptores.caminoManualApagado };
                }, function() {
                    // [#174] 500, red caída o cuerpo que no se sabe leer. Se degrada aquí dentro y
                    // NO se rechaza, para que `alFallar` quede reservado al fallo de acceso: así la
                    // distinción la sostiene la forma del código y no una bandera que alguien
                    // pueda olvidarse de mirar.
                    return {
                        activo: BLOQUEO_CUANDO_NO_SE_SABE,
                        caminoManualApagado: CAMINO_MANUAL_APAGADO_CUANDO_NO_SE_SABE,
                        avisoInterruptor: true
                    };
                });
            });
        },
        alResolver: function(r) {
            if (r.prohibido) return { component: Error403 };
            return montarCalendario(r.activo, !!r.avisoInterruptor, r.caminoManualApagado);
        },
        alFallar: function(e, routeTo) {
            // [#174] El tope saltó con el acceso ya resuelto: quien no ha contestado es el
            // interruptor. Se monta el calendario con el valor restrictivo y su aviso.
            if (accesoResuelto(routeTo)) {
                return montarCalendario(BLOQUEO_CUANDO_NO_SE_SABE, true, CAMINO_MANUAL_APAGADO_CUANDO_NO_SE_SABE);
            }
            // Lo que ha fallado o expirado es la comprobación de acceso. NO se monta el calendario
            // con los permisos de la sesión anterior: esa equivalencia es justo lo que esta barrera
            // viene a eliminar, y no saber quién mira no es un dato que falte.
            return {
                component: CalendarioNoDisponible,
                props: {
                    urlCalendario: routeTo.url,
                    motivo: textoDelFallo(e, 'No se ha podido comprobar quién está usando la aplicación.')
                }
            };
        }
    });
}

export default barreraBloqueoVacaciones;
